/**
 * L'orologio del server, non quello dell'apparecchio.
 *
 * Un box TV da quaranta euro può avere l'orologio sbagliato di ore, e non c'è modo di
 * accorgersene da qui. Il gateway manda `now` dentro le risposte che contengono orari
 * (05-CONTRATTO-API §6.1); da quel valore si ricava uno scarto, e da lì in poi ogni
 * domanda «che ore sono» passa da qui.
 *
 * Lo scarto si applica alla lettura monotòna dell'apparecchio — non alla sua data — così
 * un aggiustamento dell'orologio di sistema a metà visione non sposta il verdetto.
 */
export interface MonotonicSource {
  /** Millisecondi da un'origine arbitraria ma monotòna (`performance.now()` sul web). */
  elapsedMs(): number;
}

export class ServerClock {
  private offsetMs: number | null = null;
  private syncedAtElapsedMs = 0;

  constructor(private readonly source: MonotonicSource) {}

  /** Da chiamare con l'istante del server appena una risposta lo porta. */
  sync(serverNowMs: number): void {
    this.offsetMs = serverNowMs;
    this.syncedAtElapsedMs = this.source.elapsedMs();
  }

  get isSynced(): boolean {
    return this.offsetMs !== null;
  }

  /**
   * L'istante corrente secondo il server.
   *
   * Solleva se non si è mai sincronizzato. Non ripiega sull'orologio dell'apparecchio:
   * un verdetto «è in diretta» calcolato su un orologio sbagliato di sei ore è peggio
   * di un errore, perché nessuno lo vede.
   */
  nowMs(): number {
    if (this.offsetMs === null) {
      throw new Error('ServerClock.nowMs before sync');
    }
    return this.offsetMs + (this.source.elapsedMs() - this.syncedAtElapsedMs);
  }
}
